import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Center,
  Collapse,
  Divider,
  Flex,
  Heading,
  useBreakpointValue,
  useDisclosure,
} from '@chakra-ui/react';
import { Link, useSearchParams } from 'react-router-dom';

import { WATTWATCHERS_MANUFACTURER_ID } from 'clipsal-cortex-utils/src/constants/common-constants';

import { MobileTopNav } from '../../../../common/components/MobileTopNav';
import { BOTTOM_NAV_HEIGHT } from '../../../../common/constants';
import { LogTable } from './LogTable';
import { MeterConfigurationChart } from './meter-chart/MeterConfigurationChart';
import { SiteMeterData } from './meter-config-types';
import { MeterDetails } from './MeterDetails';
import { MeterSearchForm } from './MeterSearchForm';
import { SiteDetails } from './SiteDetails';

export const MeterConfiguration = () => {
  const [urlParams] = useSearchParams();
  const isMobileViewport = useBreakpointValue({ base: true, xl: false }, { ssr: false });
  const isWattwatchersMeter = useMemo(
    () => urlParams.get('manufacturerId') === WATTWATCHERS_MANUFACTURER_ID.toString(),
    [urlParams]
  );
  const { isOpen, onToggle } = useDisclosure();
  const [selectedMeter, setSelectedMeter] = useState<SiteMeterData>({
    siteId: Number(urlParams.get('siteId') || 0),
    siteName: urlParams.get('siteName') || '',
    serialNumber: urlParams.get('serialNumber') || '',
    siteDeviceId: Number(urlParams.get('siteDeviceId') || 0),
    siteIdentifier: urlParams.get('siteIdentifier') || '',
    manufacturerId: Number(urlParams.get('manufacturerId') || WATTWATCHERS_MANUFACTURER_ID),
    model: urlParams.get('model') || '',
    manufacturerName: urlParams.get('manufacturerName') || '',
  });

  const handleUpdateSelectedMeter = (newMeter: SiteMeterData) => {
    setSelectedMeter(newMeter);
  };

  return (
    <Box px={[0, 0, 10]}>
      {isMobileViewport ? (
        <MobileTopNav title={'Meter Configuration'} backURL={'/account/home'} />
      ) : (
        <Button as={Link} to={'/account/home'} mt={3}>
          Back
        </Button>
      )}
      <Center>
        <Box
          w={isMobileViewport ? '100%' : '90%'}
          minH={isMobileViewport ? '90vh' : '60vh'}
          bg={'white'}
          _dark={{ bg: 'gray.900' }}
          borderRadius={isMobileViewport ? 0 : 10}
          overflow="hidden"
        >
          {isMobileViewport || (
            <Heading mb={5} size={'lg'} paddingLeft={isMobileViewport ? 0 : 6} paddingTop={isMobileViewport ? 0 : 6}>
              Meter Configuration
            </Heading>
          )}
          <Divider />

          <MeterSearchForm {...{ selectedMeter, onUpdateSelectedMeter: handleUpdateSelectedMeter }} />

          {!!selectedMeter.siteIdentifier && (
            <Box mb={isMobileViewport ? BOTTOM_NAV_HEIGHT : undefined}>
              <Divider mt={4} />

              <Flex mx={4} my={4} flexDirection={'column'}>
                <MeterDetails selectedMeter={selectedMeter} />
                <Divider my={4} />
                <SiteDetails selectedMeter={selectedMeter} />
                <Divider my={4} />
                <Heading size="md" fontWeight={600} mb={4}>
                  Visualise Meter Configuration
                </Heading>
                <MeterConfigurationChart selectedMeter={selectedMeter} />
                <Divider my={4} />

                {/* Currently logs endpoint is configured for wattwatchers only */}
                {isWattwatchersMeter && (
                  <>
                    <Button onClick={onToggle} w="fit-content" mb={4} data-testid="show-log-btn">
                      {isOpen ? 'Close' : 'View'} Logs
                    </Button>

                    <Collapse in={isOpen} animateOpacity>
                      {isOpen && <LogTable selectedMeter={selectedMeter} />}
                    </Collapse>
                  </>
                )}
              </Flex>
            </Box>
          )}
        </Box>
      </Center>
    </Box>
  );
};
